import React, { useEffect, useMemo, useRef, useState } from "react";
import PhoneInput from "react-phone-number-input";
import {
  parsePhoneNumberFromString,
  getCountryCallingCode,
} from "libphonenumber-js";
import "react-phone-number-input/style.css"; // base styles & flags
import "./phonenumberpicker.css";

/* ---------------- helpers ---------------- */
export function splitPhone(e164OrPartial, fallbackIso) {
  try {
    const p = parsePhoneNumberFromString(e164OrPartial || "");
    if (!p)
      return {
        e164: e164OrPartial || "",
        countryIso: (fallbackIso || "").toUpperCase(),
        digits: "",
      };
    return {
      e164: p.number, // +CCCNNNN...
      countryIso: (p.country || fallbackIso || "").toUpperCase(),
      digits: p.nationalNumber || "", // national digits (no country calling code)
    };
  } catch {
    return {
      e164: e164OrPartial || "",
      countryIso: (fallbackIso || "").toUpperCase(),
      digits: "",
    };
  }
}

export function toE164FromParts(iso, digits) {
  const cleanIso = (iso || "").toUpperCase();
  const cleanDigits = String(digits || "").replace(/\D/g, "");
  if (!cleanIso || !cleanDigits) return "";
  try {
    const code = getCountryCallingCode(cleanIso);
    return `+${code}${cleanDigits}`;
  } catch {
    return "";
  }
}

/* ---------------- Custom Country Select ----------------
   - Trigger: flag only (no country name)
   - Menu: search input + list with flag, full name, dial code
   - Hardened against invalid/missing options
---------------------------------------------------------*/
function CountrySelect({
  value,
  onChange,
  options = [],
  labels = {},
  iconComponent: FlagIcon,
  showDial = false,
}) {
  // Filter to valid ISO-2 options only (guard against undefined)
  const optionsSafe = React.useMemo(
    () =>
      (options || []).filter(
        (o) =>
          o && typeof o.value === "string" && /^[A-Za-z]{2}$/.test(o.value),
      ),
    [options],
  );

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const selected = optionsSafe.find((o) => o.value === value) || optionsSafe[0];

  const selectedLabel = selected
    ? labels[selected.value] || selected.label
    : "";

  const safeCallingCode = (iso) => {
    try {
      if (typeof iso !== "string" || !/^[A-Za-z]{2}$/.test(iso)) return "";
      return String(getCountryCallingCode(iso));
    } catch {
      return "";
    }
  };

  // helper: fold accents & lowercase
  const norm = (s = "") =>
    s
      ?.normalize?.("NFD")
      ?.replace(/\p{Diacritic}/gu, "")
      ?.toLowerCase?.() ?? String(s || "").toLowerCase();

  const filtered = React.useMemo(() => {
    const raw = q.trim();
    const s = norm(raw.replace(/^\+/, "")); // allow typing +92 etc.
    if (!s) return optionsSafe;

    const rank = (opt) => {
      const name = norm(labels[opt.value] || opt.label || "");
      const tokens = name.split(/[\s-]+/);
      const iso = norm(opt.value);
      const code = safeCallingCode(opt.value);

      const startsName = name.startsWith(s) ? 50 : 0; // Pakistan
      const startsAnyToken = tokens.some((t) => t.startsWith(s)) ? 40 : 0; // Papua New Guinea ("pa" matches token)
      const isoStarts = iso.startsWith(s) ? 35 : 0; // pk / pa
      const codeStarts =
        code.startsWith(s) || `+${code}`.startsWith(raw) ? 30 : 0;
      const containsWord = tokens.some((t) => t.includes(s)) ? 15 : 0; // in-word match
      const containsAnywhere = name.includes(s) ? 5 : 0;

      return (
        startsName +
        startsAnyToken +
        isoStarts +
        codeStarts +
        containsWord +
        containsAnywhere
      );
    };

    return optionsSafe
      .map((o) => [o, rank(o)])
      .filter(([, r]) => r > 0)
      .sort(
        (a, b) =>
          b[1] - a[1] ||
          (labels[a[0].value] || a[0].label || "").localeCompare(
            labels[b[0].value] || b[0].label || "",
          ),
      )
      .map(([o]) => o);
  }, [q, optionsSafe, labels]);

  return (
    <div className="relative">
      {/* Trigger: flag only */}
      <button
        type="button"
        className="flex items-center gap-2 pl-3"
        onClick={() => {
          setOpen((o) => !o);
          setQ("");
        }}
        aria-label={selectedLabel || "Select country"}
      >
        {FlagIcon && selected?.value && (
          <FlagIcon country={selected.value} label={selectedLabel} />
        )}
        {showDial && selected?.value && safeCallingCode(selected.value) && (
          <span className="text-sm font-semibold tabular-nums text-gray-800">
            +{safeCallingCode(selected.value)}
          </span>
        )}
        {/* keep label for screen readers only */}
        <span className="sr-only">{selectedLabel}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`ml-1 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Menu */}
      {open && (
        <div
          className="absolute left-0 z-50 mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
          style={{
            width: "min(20rem, calc(100vw - 64px))",
            maxWidth: "calc(100vw - 64px)",
          }}
        >
          {/* Search box */}
          <div className="border-b border-gray-200 p-2">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search country, ISO, or +code"
              className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-black/10"
              type="text"
            />
          </div>

          {/* Options list */}
          <div className="max-h-72 overflow-auto py-1" role="listbox">
            {filtered.map((opt) => {
              const label = labels[opt.value] || opt.label || opt.value;
              const code = safeCallingCode(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={opt.value === value}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                  onMouseDown={(e) => {
                    // Prevent the trigger from losing focus before we commit selection.
                    e.preventDefault();
                    onChange(opt.value); // ← notifies react-phone-number-input immediately
                    setOpen(false);
                    setQ("");
                  }}
                >
                  {FlagIcon && <FlagIcon country={opt.value} label={label} />}
                  <span className="flex-1">{label}</span>
                  <span className="text-gray-500">
                    {code ? `+${code}` : ""}
                  </span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- PhoneNumberPicker ---------------- */
// ...imports stay the same

export default function PhoneNumberPicker({
  id = "phone",
  label = "Phone number",
  required = false,
  placeholder = "Enter phone number",
  defaultCountry = "PK",
  valueE164,
  valueDigits,
  countryIso,
  onChange,
  // When true: show the dial code inside the country chip and render the input
  // in NATIONAL format (no +code prefix), matching the settings mock. Defaults
  // to false so every other usage keeps the original international format.
  showDialInChip = false,
}) {
  // ----- existing initialE164 / val logic (unchanged) -----
  const initialE164 = useMemo(() => {
    if (valueE164) return valueE164;
    if (countryIso && (valueDigits || valueDigits === "")) {
      return toE164FromParts(countryIso, valueDigits);
    }
    return "";
  }, [valueE164, valueDigits, countryIso]);

  const [val, setVal] = useState(initialE164);

  useEffect(() => {
    const next = valueE164 ?? toE164FromParts(countryIso, valueDigits);
    setVal((prev) => (prev === next ? prev : next || ""));
  }, [valueE164, valueDigits, countryIso]);

  function handleChange(nextVal) {
    const nv = nextVal || "";
    setVal(nv);

    // Use the ref (latest ISO even during the same tick)
    const fallback = isoRef.current || countryIso || defaultCountry;
    const { e164, countryIso: iso, digits } = splitPhone(nv, fallback);

    // If typing changed the ISO (e.g., new +code), sync both ref and state
    if (iso && iso !== isoRef.current) {
      isoRef.current = iso;
      setUiCountry(iso);
    }
    onChange?.({ e164, countryIso: iso, digits });
  }

  // ----- NEW: local, immediate UI country state -----
  const resolvedDefaultCountry = (
    countryIso ||
    defaultCountry ||
    "PK"
  ).toUpperCase();
  const safeResolved = /^[A-Z]{2}$/.test(resolvedDefaultCountry)
    ? resolvedDefaultCountry
    : "PK";

  const [uiCountry, setUiCountry] = useState(safeResolved);
  const isoRef = useRef(safeResolved);
  useEffect(() => {
    isoRef.current = uiCountry;
  }, [uiCountry]);

  // keep local UI country in sync if parent changes it (after save/refresh)
  useEffect(() => {
    if (safeResolved !== uiCountry) setUiCountry(safeResolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeResolved]);

  // Stable country-select component that forwards the showDial flag.
  const CountrySelectComponent = useMemo(
    () => (props) => <CountrySelect {...props} showDial={showDialInChip} />,
    [showDialInChip],
  );

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={id}
          className="mb-1 block text-sm font-medium text-gray-800"
        >
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </label>
      )}

      <div className="PhoneField">
        <PhoneInput
          id={id}
          name={id}
          aria-label={label}
          international={!showDialInChip}
          defaultCountry={uiCountry}
          country={uiCountry}
          countryCallingCodeEditable={false}
          value={val}
          onChange={handleChange}
          onCountryChange={(nextIso) => {
            const iso = (nextIso || "").toUpperCase();

            isoRef.current = iso; // ← set synchronously so handleChange won't use stale ISO
            setUiCountry(iso);

            const { digits } = splitPhone(
              val,
              iso || isoRef.current || defaultCountry,
            );
            const e164 = toE164FromParts(iso, digits);
            setVal(e164);
            onChange?.({ e164, countryIso: iso, digits });
          }}
          placeholder={placeholder}
          className="PhoneInput"
          inputMode="tel"
          autoComplete="tel"
          required={required}
          countrySelectComponent={CountrySelectComponent}
        />
      </div>
    </div>
  );
}
